import { supabase, DASHBOARD_LABEL } from './supabase'
import { readSession } from './auth'
import { brandParam, readBrandFilter } from './brand'
import type {
  Mismatch,
  HealthMetric,
  MatchedRow,
  OmegaRow,
  PaycomRow,
  ResolutionType,
  Anomaly,
} from '../types'

function session() {
  const s = readSession()
  if (!s) throw new Error('not authenticated')
  return s
}

export async function fetchActiveMismatches(): Promise<Mismatch[]> {
  const s = session()
  const { data, error } = await supabase.rpc('recon_list_active_mismatches', {
    p_label: DASHBOARD_LABEL,
    p_password: s.password,
    p_brand: brandParam(readBrandFilter()),
  })
  if (error) throw error
  return (data ?? []) as Mismatch[]
}

export async function fetchHistory(limit = 200): Promise<Mismatch[]> {
  const s = session()
  const { data, error } = await supabase.rpc('recon_list_history', {
    p_label: DASHBOARD_LABEL,
    p_password: s.password,
    p_brand: brandParam(readBrandFilter()),
    p_limit: limit,
  })
  if (error) throw error
  return (data ?? []) as Mismatch[]
}

export async function fetchMatched(limit = 200): Promise<MatchedRow[]> {
  const s = session()
  const { data, error } = await supabase.rpc('recon_list_matched', {
    p_label: DASHBOARD_LABEL,
    p_password: s.password,
    p_brand: brandParam(readBrandFilter()),
    p_limit: limit,
  })
  if (error) throw error
  return (data ?? []) as MatchedRow[]
}

export async function fetchOmegaRaw(
  limit = 1000,
  from?: string,
  to?: string,
): Promise<OmegaRow[]> {
  const s = session()
  const { data, error } = await supabase.rpc('recon_list_omega_raw', {
    p_label: DASHBOARD_LABEL,
    p_password: s.password,
    p_limit: limit,
    p_from: from ?? null,
    p_to: to ?? null,
    p_brand: brandParam(readBrandFilter()),
  })
  if (error) throw error
  return (data ?? []) as OmegaRow[]
}

export async function fetchPaycomRaw(
  limit = 1000,
  from?: string,
  to?: string,
): Promise<PaycomRow[]> {
  const s = session()
  const { data, error } = await supabase.rpc('recon_list_paycom_raw', {
    p_label: DASHBOARD_LABEL,
    p_password: s.password,
    p_limit: limit,
    p_from: from ?? null,
    p_to: to ?? null,
    p_brand: brandParam(readBrandFilter()),
  })
  if (error) throw error
  return (data ?? []) as PaycomRow[]
}

export async function fetchAnomalies(): Promise<Anomaly[]> {
  const s = session()
  const { data, error } = await supabase.rpc('recon_list_anomalies', {
    p_label: DASHBOARD_LABEL,
    p_password: s.password,
  })
  if (error) throw error
  return (data ?? []) as Anomaly[]
}

export async function fetchHealth(): Promise<HealthMetric[]> {
  const s = session()
  const { data, error } = await supabase.rpc('recon_health_snapshot', {
    p_label: DASHBOARD_LABEL,
    p_password: s.password,
    p_brand: brandParam(readBrandFilter()),
  })
  if (error) throw error
  return (data ?? []) as HealthMetric[]
}

export async function acknowledge(id: number): Promise<Mismatch> {
  const s = session()
  const { data, error } = await supabase.rpc('recon_acknowledge_mismatch', {
    p_label: DASHBOARD_LABEL,
    p_password: s.password,
    p_id: id,
    p_user: s.user,
  })
  if (error) throw error
  return data as Mismatch
}

export async function resolve(
  id: number,
  resolution_type: ResolutionType,
  resolution_notes: string,
): Promise<Mismatch> {
  const s = session()
  const { data, error } = await supabase.rpc('recon_resolve_mismatch', {
    p_label: DASHBOARD_LABEL,
    p_password: s.password,
    p_id: id,
    p_user: s.user,
    p_resolution_type: resolution_type,
    p_resolution_notes: resolution_notes,
  })
  if (error) throw error
  return data as Mismatch
}
